/**
 * The webmail search bar (top bar, like OWA): one field for text and operators (`from:`,
 * `subject:` …) with autocomplete for operator names and people, and the shortcut hint. On phones it is a search icon that opens the same field full width.
 * Where to search (scope) and refining an active search (attachments, unread, date …) happen in the result list.
 */
import { useI18n, type MessageKey } from '@platform/i18n';
import { BREAKPOINTS, Button, cn, Kbd, useMediaQuery, useUiStrings, type PersonEntry } from '@platform/ui';
import { ArrowLeftIcon, SearchIcon, SlidersHorizontalIcon } from 'lucide-react';
import { useEffect, useId, useImperativeHandle, useMemo, useRef, useState, type Ref } from 'react';
import { api } from './api';
import {
  applyToWord,
  commonOperators,
  intentAt,
  operatorSuggestions,
  operatorText,
  PERSON_OPERATORS,
  valueChoices,
  type SearchLocale,
  type Word,
} from './search-suggest';

export interface MailSearchBarHandle {
  /** Focuses the field (on phones: opens the full-width search first). */
  focus: () => void;
}

export interface MailSearchBarProps {
  value: string;
  onChange: (value: string) => void;
  /** Enter: run the search for this text. */
  onSubmit: (query: string) => void;
  /** Escape in an empty list of suggestions: clear the text and the search. */
  onClear: () => void;
  /** A search is running (highlights the phone icon). */
  active: boolean;
  /** Opens the full filter panel ("More filters…" in the tips). */
  onMoreFilters: () => void;
  ref?: Ref<MailSearchBarHandle>;
}

type Option =
  | { id: string; kind: 'operator'; keyword: string; hint: string; insert: string }
  | { id: string; kind: 'value'; label: string; insert: string }
  | { id: string; kind: 'person'; name: string; email: string; insert: string }
  | { id: string; kind: 'more' };

const PEOPLE_DELAY_MS = 200;

export function MailSearchBar({ ref, ...props }: MailSearchBarProps) {
  const { t } = useI18n();
  const isDesktop = useMediaQuery(BREAKPOINTS.md);
  const [overlay, setOverlay] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    focus: () => (isDesktop ? input.current?.focus() : setOverlay(true)),
  }), [isDesktop]);

  if (isDesktop) {
    return (
      <div className="h-control w-full max-w-md">
        <SearchField {...props} inputRef={input} />
      </div>
    );
  }
  return (
    <>
      <Button
        variant={props.active ? 'secondary' : 'ghost'}
        size="icon"
        aria-label={t('webmail.searchPlaceholder')}
        onClick={() => setOverlay(true)}
      >
        <SearchIcon />
      </Button>
      {overlay && (
        <div role="dialog" aria-modal="true" aria-label={t('webmail.searchPlaceholder')} className="fixed inset-0 z-50 flex flex-col gap-2 bg-background p-2">
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" aria-label={t('webmail.searchBar.close')} onClick={() => setOverlay(false)}>
              <ArrowLeftIcon />
            </Button>
            <SearchField {...props} inputRef={input} autoFocus onDone={() => setOverlay(false)} onEscape={() => setOverlay(false)} />
          </div>
        </div>
      )}
    </>
  );
}

interface SearchFieldProps extends Omit<MailSearchBarProps, 'active' | 'ref'> {
  inputRef: React.RefObject<HTMLInputElement | null>;
  autoFocus?: boolean;
  /** After a submit or a "More filters" choice (closes the phone overlay). */
  onDone?: () => void;
  /** Escape with nothing to dismiss (phone overlay: close it). */
  onEscape?: () => void;
}

function SearchField({ value, onChange, onSubmit, onClear, onMoreFilters, inputRef, autoFocus, onDone, onEscape }: SearchFieldProps) {
  const { t, locale } = useI18n();
  const strings = useUiStrings();
  const searchLocale: SearchLocale = locale.startsWith('de') ? 'de' : 'en';
  const listId = useId();
  const [caret, setCaret] = useState(value.length);
  const [focused, setFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [people, setPeople] = useState<{ query: string; entries: PersonEntry[] }>();

  const found = useMemo(() => intentAt(value, caret, searchLocale), [value, caret, searchLocale]);
  const peopleQuery = found?.intent.kind === 'value' && PERSON_OPERATORS.includes(found.intent.operator) ? found.intent.partial : undefined;

  useEffect(() => {
    if (peopleQuery === undefined) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      api.suggestRecipients(peopleQuery, controller.signal).then(
        (entries) => setPeople({ query: peopleQuery, entries }),
        () => undefined,
      );
    }, PEOPLE_DELAY_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [peopleQuery]);

  const options = useMemo<Option[]>(() => {
    const hint = (operator: string) => t(`webmail.searchBar.operators.${operator}` as MessageKey);
    if (!found) {
      if (value.trim()) return [];
      return [
        ...commonOperators(searchLocale).map((suggestion): Option => ({
          id: `op-${suggestion.operator}`, kind: 'operator', keyword: suggestion.keyword, hint: hint(suggestion.operator), insert: operatorText(suggestion),
        })),
        { id: 'more', kind: 'more' },
      ];
    }
    const { intent } = found;
    if (intent.kind === 'operator') {
      return operatorSuggestions(intent.prefix, searchLocale).slice(0, 7).map((suggestion): Option => ({
        id: `op-${suggestion.operator}`, kind: 'operator', keyword: suggestion.keyword, hint: hint(suggestion.operator), insert: operatorText(suggestion),
      }));
    }
    const keyword = found.word.text.slice(0, found.word.text.indexOf(':'));
    if (PERSON_OPERATORS.includes(intent.operator)) {
      return (people?.query === intent.partial ? people.entries : []).slice(0, 7).map((entry): Option => ({
        id: `person-${entry.key}`, kind: 'person', name: entry.name ?? entry.email, email: entry.email, insert: `${keyword}:${entry.email}`,
      }));
    }
    return valueChoices(intent.operator, searchLocale)
      .filter((choice) => choice.replace(/^"/, '').startsWith(intent.partial.toLowerCase()) && choice.replace(/"/g, '') !== intent.partial)
      .map((choice): Option => ({ id: `value-${choice}`, kind: 'value', label: choice.replace(/"/g, ''), insert: `${keyword}:${choice}` }));
  }, [found, people, searchLocale, t, value]);

  const open = focused && !dismissed && options.length > 0;
  const activeOption = open ? options[activeIndex] : undefined;

  const reveal = (nextCaret: number) => {
    setCaret(nextCaret);
    setDismissed(false);
    setActiveIndex(-1);
  };

  const choose = (option: Option) => {
    if (option.kind === 'more') {
      setDismissed(true);
      onMoreFilters();
      onDone?.();
      return;
    }
    const word: Word = found?.word ?? { start: value.length, end: value.length, text: '' };
    const done = option.kind !== 'operator';
    const next = applyToWord(value, word, option.insert, done);
    onChange(next.value);
    reveal(next.caret);
    // Operators keep the list open for their values; the caret goes behind the inserted text.
    requestAnimationFrame(() => inputRef.current?.setSelectionRange(next.caret, next.caret));
    inputRef.current?.focus();
  };

  const submit = () => {
    setDismissed(true);
    onSubmit(value.trim());
    onDone?.();
  };

  return (
    <div className="relative w-full min-w-0">
      <div
        className={cn(
          'flex h-control items-center gap-1.5 rounded-md border border-input bg-transparent pl-2.5 pr-1 shadow-sm transition-colors dark:bg-input/30',
          'focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50',
        )}
      >
        <SearchIcon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
        <input
          ref={inputRef}
          type="search"
          value={value}
          autoFocus={autoFocus}
          autoComplete="off"
          spellCheck={false}
          onChange={(event) => {
            onChange(event.target.value);
            reveal(event.target.selectionStart ?? event.target.value.length);
          }}
          onSelect={(event) => setCaret(event.currentTarget.selectionStart ?? value.length)}
          onFocus={() => { setFocused(true); setDismissed(false); }}
          onBlur={() => setFocused(false)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              if (options.length === 0) return;
              event.preventDefault();
              setDismissed(false);
              setActiveIndex((index) => (event.key === 'ArrowDown' ? (index + 1) % options.length : index <= 0 ? options.length - 1 : index - 1));
            } else if (event.key === 'Enter') {
              event.preventDefault();
              if (activeOption) choose(activeOption);
              else submit();
            } else if (event.key === 'Tab' && open) {
              event.preventDefault();
              choose(activeOption ?? options[0]!);
            } else if (event.key === 'Escape') {
              if (open) { event.stopPropagation(); setDismissed(true); return; }
              onClear();
              onEscape?.();
            }
          }}
          placeholder={t('webmail.searchPlaceholder')}
          aria-label={t('webmail.searchPlaceholder')}
          aria-autocomplete="list"
          aria-controls={open ? listId : undefined}
          aria-activedescendant={activeOption ? `${listId}-${activeOption.id}` : undefined}
          className="h-full min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        />
        {!value && <Kbd className="hidden shrink-0 sm:inline-flex">{strings.commandShortcut}</Kbd>}
      </div>
      {open && (
        <ul
          id={listId}
          role="listbox"
          aria-label={t('webmail.searchBar.suggestions')}
          className="absolute top-full right-0 left-0 z-50 mt-1 max-h-80 overflow-y-auto rounded-md border bg-popover p-1 text-sm text-popover-foreground shadow-md"
        >
          {options.map((option, index) => (
            <li
              key={option.id}
              id={`${listId}-${option.id}`}
              role="option"
              aria-selected={index === activeIndex}
              // Keeps the focus in the field, so the list does not close before the click lands.
              onMouseDown={(event) => { event.preventDefault(); choose(option); }}
              onMouseMove={() => setActiveIndex(index)}
              className={cn('flex cursor-default items-baseline gap-2 rounded-sm px-2 py-1.5', index === activeIndex && 'bg-accent text-accent-foreground')}
            >
              {option.kind === 'operator' && (
                <>
                  <span className="font-medium">{option.insert}</span>
                  <span className="truncate text-xs text-muted-foreground">{option.hint}</span>
                </>
              )}
              {option.kind === 'value' && <span>{option.label}</span>}
              {option.kind === 'person' && (
                <>
                  <span className="truncate font-medium">{option.name}</span>
                  {option.name !== option.email && <span className="truncate text-xs text-muted-foreground">{option.email}</span>}
                </>
              )}
              {option.kind === 'more' && (
                <span className="flex items-center gap-2">
                  <SlidersHorizontalIcon aria-hidden className="size-3.5 text-muted-foreground" />
                  {t('webmail.searchAdvanced.open')}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
